/* eslint-disable no-restricted-imports */
import {
	Box,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Stack,
	Typography,
} from '@mui/material';
import { GOOGLE_MAP_REGION, STORE_LOCATOR_LIBRARY } from '@/data/constants/storeLocator';
import { GoogleMap, MarkerF, useJsApiLoader } from '@react-google-maps/api';
import { useSettings } from '@/data/Settings';
import { DpH2 } from '@/components/blocks/DpLibrary/DPText';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { calculateMapCenterZoom } from 'integration/utils/calculateMapCenterZoom';
import { EMPTY_STRING } from '@/data/constants/marketing';
import {
	DpCardMapFrameSX,
	DpCardMapInfoFrameSX,
	DpCardMapInfoTitleSX,
	DpCardMapInfoWindowNameSX,
	DpCardMapStyle,
} from './styles';
import { combineSX } from '@/utils/combineSX';
import { stylehowToGet } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import PhoneIcon from '@mui/icons-material/Phone';
import CloseIcon from '@mui/icons-material/Close';
import { AccountDropdownIcon } from '@/components/content/Account/parts/icons/AccountDropdownIcon';
import { DpContainer } from '../../DpContainer';

type MapStoreLocation = {
	lat: number;
	lng: number;
};

type MapStore = {
	uuid: string;
	state: string;
	name: string;
	address: string;
	phone?: string;
	location: MapStoreLocation;
};

const MAP_STORES: MapStore[] = [
	{
		uuid: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
		state: 'Aguascalientes',
		name: 'Plaza Altaria',
		address:
			'Blvd. A. Zacatecas No. 849 Int. Centro Comercial ALTARIA Loc.1005. Col. Trojes de Alonso.,20311.',
		phone: '(449) 993-3773',
		location: { lat: 21.92381, lng: -102.28983 },
	},
	{
		uuid: '787b80d2-bb49-45b4-bd93-e4dc2425ecd6',
		state: 'Aguascalientes',
		name: 'El Parian',
		address: 'Centro Comercial el Parian, Loc. 40-49. Col. Centro.,20000.',
		phone: '(449) 994-2318',
		location: { lat: 21.88359, lng: -102.29536 },
	},
	{
		uuid: '89755a7a-fb37-4b9c-a55c-26f26e94a55b',
		state: 'Aguascalientes',
		name: 'Allende',
		address: 'Andador Allende # 205. Col. Centro.,20000.',
		phone: '(449) 916-5235',
		location: { lat: 21.9017207, lng: -102.360616 },
	},
	{
		uuid: '86d00e48-2a45-468e-b423-0b359f58f20e',
		state: 'Aguascalientes',
		name: 'Adana',
		address:
			'Av Tecnologico S/N interior D-5 Interior Centro Comercial Adana, Col. Ojo Caliente,20196.',
		location: { lat: 21.8798, lng: -102.26057 },
	},
	{
		uuid: '4f1036ce-88d1-47e1-8b9f-3747ac0c5b07',
		state: 'Guanajuato',
		name: 'Irapuato Guerrero',
		address: 'Guerrero 2-E. Col. Centro.,36500.',
		phone: '(462) 660-3402',
		location: { lat: 20.67554, lng: -101.34744 },
	},
	{
		uuid: '3ec570ca-48bc-4cfd-b809-b445e365c6b2',
		state: 'Guanajuato',
		name: 'Plaza Fiesta',
		address: 'Plaza Fiesta, Sección C, Local 11-13. Col. Centro.,36557.',
		phone: '(462) 660-3382',
		location: { lat: 20.67801, lng: -101.34291 },
	},
	{
		uuid: 'f7d80be5-a5af-433a-a4de-6bc38e938b46',
		state: 'Guanajuato',
		name: 'Jacarandas',
		address:
			'Av. Arboledas # 1200 Local 23-24-25 Seccion B, entre Calle Independencia y Prolongacion Blvd Lazaro Cardenas, Interior Plaza Comercial Jacarandas. Col. San Miguelito.,36557.',
		phone: '(462) 623-7405',
		location: { lat: 20.67161, lng: -101.3715 },
	},
	{
		uuid: '4f53344c-6b59-4c49-9318-0af3ae0e2fac',
		state: 'Guanajuato',
		name: 'Plaza Luciérnaga',
		address:
			'Libramiento José Manuel Zavala Zavala # 165 Int. Plaza Luciérnaga L-10-11-12. Col. Luciérnaga. San Miguel de Allende, Gto.,37745.',
		phone: '(415) 110-3191',
		location: { lat: 20.90375, lng: -100.7277 },
	},
	{
		uuid: '59857dc0-f547-4c8c-a831-e2cb0c6d301a',
		state: 'Jalisco',
		name: 'Av. Juarez Gdl',
		address: 'Av. Juárez # 145, Col. Centro.,44100.',
		phone: '(333) 614-45-39',
		location: { lat: 20.6751, lng: -103.34479 },
	},
	{
		uuid: '64d17c27-2461-4047-90c3-3f624ad25dbb',
		state: 'Jalisco',
		name: 'Mina',
		address: 'Fco. Javier Mina # 196. Col. Centro.,44100.',
		phone: '(333) 618-1172',
		location: { lat: 20.67455, lng: -103.33855 },
	},
	{
		uuid: '754a9534-1135-4206-8548-35a15851311b',
		state: 'Jalisco',
		name: 'Pedro Moreno',
		address: 'Pedro Moreno # 479. Col. Centro.,44100.',
		phone: '(333) 658-5806',
		location: { lat: 20.6757, lng: -103.34934 },
	},
	{
		uuid: 'af8f5d9b-5969-49be-81cc-1d0133c8a4ef',
		state: 'Jalisco',
		name: 'Morelos Gdl',
		address: 'Morelos # 550. Col. Centro.,44100.',
		phone: '(333) 658-5943',
		location: { lat: 20.67645, lng: -103.34947 },
	},
	{
		uuid: 'fb006055-4a15-4065-b30f-598d09e662f0',
		state: 'Jalisco',
		name: 'Hidalgo Gdl',
		address: 'Ave. Hidalgo # 505. Col. Centro.,44100.',
		phone: '(333) 658-2451',
		location: { lat: 20.6772, lng: -103.34984 },
	},
	{
		uuid: '47a5001a-ac6d-4493-bafa-fd937a458fb0',
		state: 'Jalisco',
		name: 'Dpstreet Forum Tlaquepaque',
		address:
			'Blvd. Gral. Marcelino G. Barragan Num 2077, Interior Forum Tlaquepaque, L- PB-33. Col. Prados del Nilo.,44840.',
		phone: '(331) 578-0842',
		location: { lat: 20.64748, lng: -103.32021 },
	},
	{
		uuid: '653ca51d-9137-4eae-add2-71fe5e3941fe',
		state: 'Jalisco',
		name: 'Forum Tlaquepaque',
		address:
			'Blvd. Gral. Marcelino G. Barragan Num 2077, Interior Forum Tlaquepaque, L- PB-25. Col. Prados del Nilo.,44840.',
		phone: '(333) 657-0783',
		location: { lat: 20.6483, lng: -103.32011 },
	},
	{
		uuid: '7a4f5da4-3674-43f5-ac84-f22e288c4594',
		state: 'Jalisco',
		name: 'Santa Mónica',
		address: 'Santa Monica # 14. Col. Centro.,44100.',
		phone: '(333) 613-7353',
		location: { lat: 20.67688, lng: -103.34884 },
	},
	{
		uuid: '9b82ed45-38c6-4fde-b8a4-793f91aee0b5',
		state: 'Jalisco',
		name: 'Plaza del Sol',
		address:
			'Av. Adolfo López Mateos Sur #2375 Int. Plaza del Sol L-35. Col. Ciudad del Sol.,45050.',
		phone: '(333) 121-6726',
		location: { lat: 20.65148, lng: -103.40088 },
	},
	{
		uuid: '377c8c49-ed8e-41e5-b23d-4086defb7a87',
		state: 'Jalisco',
		name: 'Gran Plaza Gdl',
		address: 'Av. Vallarta 3959 Int Gran Plaza, Local 9D. Col. Don Bosco,45049.',
		phone: '(333) 621-1503',
		location: { lat: 20.67322, lng: -103.40425 },
	},
	{
		uuid: '56a304f3-b33b-4549-a777-1c935eb267c1',
		state: 'Jalisco',
		name: 'Colon',
		address: 'Calle Colon # 130. Col. Centro,44100.',
		phone: '(333) 614-9923',
		location: { lat: 20.67482, lng: -103.34812 },
	},
	{
		uuid: 'ec201a50-585c-4467-9253-b67ce9218ff2',
		state: 'Jalisco',
		name: 'Outlet Gdl',
		address:
			'Carret. Guadalajara a Morelia Km. 12.5, Interior Plazas Oulet, L-71. Tlajomulco de Zuñiga.,45640.',
		phone: '(333) 686-2617',
		location: { lat: 20.52167, lng: -103.48361 },
	},
	{
		uuid: 'fa513562-d235-478d-ac08-934e6f953d34',
		state: 'Querétaro',
		name: 'Benito Juárez Qro',
		address: 'Juárez #11, Esq. Morelos. Col. Centro.,76000.',
		phone: '(442) 212-5391',
		location: { lat: 20.59483, lng: -100.39331 },
	},
	{
		uuid: '30d8d035-5fba-4073-92a2-ae05dd2ac9fb',
		state: 'Querétaro',
		name: 'Sendero',
		address:
			'Av. Revolución #99, Local 7 y 8, entre Geiser y Blvd. Bernardo Quintana. Col. El Rocío.,76114.',
		phone: '(442) 220-2529',
		location: { lat: 20.61778, lng: -100.43936 },
	},
	{
		uuid: '411a0ecd-f91a-497a-9eef-1b846399f249',
		state: 'Querétaro',
		name: 'Galerías San Juan del Río',
		address:
			'Carret. Panamericana Pte. #202, Int. L 214. Col. El Rocío. San Juan del Río, Qro.,76800.',
		phone: '(427) 272-5177',
		location: { lat: 20.38604, lng: -100.01227 },
	},
	{
		uuid: 'bd995ef3-db1a-4426-aaec-f244d50050a2',
		state: 'Yucatán',
		name: 'Centro Comercial Altabrisa',
		address:
			'Centro Comercial Altabrisa, int Local PA-143. Col. Altabrisa. C.P. 97130. Mérida, Yucatán,97130.',
		location: { lat: 21.01905, lng: -89.58499 },
	},
	{
		uuid: 'c2a66a6d-8688-418b-95fc-a922f345f8dc',
		state: 'Yucatán',
		name: 'Centro Comercial Patio',
		address:
			'Centro Comercial Patio, int. 22C. Col. Chuminopolis C.P. 97158. Mérida Yucatán.,97158.',
		location: { lat: 20.97572, lng: -89.59617 },
	},
	{
		uuid: '5463c5d8-ebab-4131-865d-252bf6d421cf',
		state: 'Yucatán',
		name: 'Plaza La Isla Mérida',
		address:
			'Plaza La Isla Mérida, int. PA-34. Col. Santa Gertrudis Copo C.P. 97305 Tixcuytun Mérida Yucatán.,97305.',
		location: { lat: 21.05649, lng: -89.59629 },
	},
	{
		uuid: 'df069dc7-6e62-462b-81ff-756f646eb5df',
		state: 'Yucatán',
		name: 'Mérida Centro',
		address: 'Calle 58 #532 Int.2 Col. Centro C.P. 97000 Mérida, Yucatán.,97000.',
		location: { lat: 20.96505, lng: -89.62216 },
	},
	{
		uuid: '62b48fb3-1c17-41b0-8b08-134f70c0938e',
		state: 'Zacatecas',
		name: 'Comercial Portal Fresnillo',
		address:
			'Comercial Portal Fresnillo, int. SSA18. Col. Panamericana. C.P. 99014 Centro. Fresnillo Zacatecas.,99014.',
		location: { lat: 23.1962, lng: -102.88471 },
	},
	{
		uuid: '074c2eec-4fc2-49ec-8391-d583fdafde48',
		state: 'Zacatecas',
		name: 'Fresnillo Centro',
		address: 'Calle Juárez #218. Col. Centro C.P. 99000 Fresnillo, Zacatecas.,99000.',
		location: { lat: 23.17436, lng: -102.87013 },
	},
];

const useDpCardMap = () => {
	const [mapInstance, setMapInstance] = useState<google.maps.Map>({} as google.maps.Map);
	const [clickedStore, setClickedStore] = useState<MapStore | null>(null);
	const [selectedState, setSelectedState] = useState('');

	const { settings } = useSettings();
	const { isLoaded } = useJsApiLoader({
		googleMapsApiKey: settings.mapApiKey || '',
		region: GOOGLE_MAP_REGION,
		libraries: STORE_LOCATOR_LIBRARY,
	});

	const handleMapOnLoad = (mapInstance: google.maps.Map) => {
		setMapInstance(mapInstance);
	};

	const handleStateChange = (event: SelectChangeEvent) => {
		setSelectedState(event.target.value);
	};

	const states = useMemo(() => {
		const states = MAP_STORES.map((store) => store.state);
		return Array.from(new Set(states));
	}, []);

	const stores = useMemo(() => {
		if (selectedState === EMPTY_STRING) {
			return MAP_STORES;
		}

		return MAP_STORES.filter((store) => store.state === selectedState);
	}, [selectedState]);

	const { mapCenter, mapZoom } = useMemo(() => {
		const locations = stores.map((store) => store.location);
		const { center, zoom } = calculateMapCenterZoom(locations);
		return { mapCenter: center, mapZoom: zoom };
	}, [stores]);

	const handleStoreClick = (store: MapStore) => () => {
		setClickedStore(store);
	};

	const handleStoresDeselect = useCallback(() => {
		setClickedStore(null);
	}, []);

	return {
		isLoaded,
		mapInstance,
		mapCenter,
		mapZoom,
		stores,
		states,
		clickedStore,
		selectedState,
		handleMapOnLoad,
		handleStateChange,
		handleStoreClick,
		handleStoresDeselect,
	};
};

export const DpCardMap = () => {
	const {
		isLoaded,
		clickedStore,
		stores,
		states,
		mapCenter,
		mapZoom,
		selectedState,
		handleMapOnLoad,
		handleStateChange,
		handleStoreClick,
		handleStoresDeselect,
	} = useDpCardMap();

	const [selectedStoreUuid, setSelectedStoreUuid] = useState<string | null>(null);
	const [closeInfo, setCloseInfo] = useState(true);
	const CustomIconComponent = (props: any) => <AccountDropdownIcon {...props} />;
	const [selectedStore, setSelectedStore] = useState<string>('');

	const handleMarkerClick = (uuid: string, store: MapStore) => {
		handleStoreClick(store)();
		setSelectedStoreUuid(uuid);
		setCloseInfo(false);
	};

	useEffect(() => {
		if (stores.length > 0) {
			setSelectedStore(stores[0].uuid);
			const findStore = stores.find((s) => s.uuid === stores[0].uuid);
			if (findStore) {
				handleMarkerClick(findStore.uuid, findStore);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [stores]);

	useEffect(() => {
		if (closeInfo) {
			handleStoresDeselect();
		}
	}, [closeInfo, handleStoresDeselect]);

	const handleStore = (store: string) => {
		const findStore = stores.find((s) => s.uuid === store);
		if (findStore) {
			handleMarkerClick(findStore.uuid, findStore);

			setSelectedStore(findStore.uuid);
		}
	};
	if (!isLoaded) {
		return null;
	}

	return (
		<DpContainer sx={DpCardMapFrameSX}>
			<Grid container spacing={{ xs: 3, md: 12 }}>
				<Grid item xs={12} md={6}>
					<Box sx={{ position: 'relative', height: '100%' }}>
						<GoogleMap
							mapContainerStyle={DpCardMapStyle}
							zoom={mapZoom}
							center={mapCenter}
							options={{
								disableDefaultUI: true,
								mapId: 'dportenis',
							}}
							onLoad={handleMapOnLoad}
						>
							{MAP_STORES.map((store) => {
								const markerIcon =
									selectedStoreUuid === store.uuid
										? '/images/pinSelectDPBlack.svg'
										: '/images/pinDPBlack.svg';

								return (
									<MarkerF
										key={store.uuid}
										position={store.location}
										icon={markerIcon}
										onClick={() => handleMarkerClick(store.uuid, store)}
									/>
								);
							})}
						</GoogleMap>

						{clickedStore && !closeInfo ? (
							<Box
								sx={{
									position: 'absolute',
									bottom: 0,
									left: 0,
									right: 0,
									backgroundColor: '#fff',
									borderTop: '1px solid #ccc',
									p: 1,
								}}
							>
								<Box sx={{ position: 'relative' }}>
									<Box sx={{ position: 'absolute', right: '-5px', top: '-5px' }}>
										<IconButton onClick={() => setCloseInfo(true)}>
											<CloseIcon />
										</IconButton>
									</Box>
									<Stack>
										<Typography variant="h6" sx={DpCardMapInfoWindowNameSX}>
											{clickedStore.name}
										</Typography>
										<Typography>{clickedStore.address}</Typography>
										{clickedStore.phone ? (
											<Typography sx={combineSX([stylehowToGet, { marginTop: '2px' }])}>
												<a
													href={`tel:+52${clickedStore.phone}`}
													style={{ textDecoration: 'none', color: 'inherit' }}
												>
													<PhoneIcon sx={{ marginBottom: '-6px' }} />
													{clickedStore.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}
												</a>
											</Typography>
										) : null}
									</Stack>
								</Box>
							</Box>
						) : null}
					</Box>
				</Grid>

				<Grid item xs={12} md={6} sx={DpCardMapInfoFrameSX}>
					<DpH2 sx={DpCardMapInfoTitleSX}>
						{'EN ESTAS TIENDAS PUEDES TRAMITAR, COMPRAR Y PAGAR CON TU TARJETA DE CRÉDITO DPCARD.'}
					</DpH2>

					<FormControl variant="outlined" fullWidth>
						{!selectedState ? (
							<InputLabel
								htmlFor="numberOfInstallments"
								sx={{
									transform: 'none',
									position: 'absolute',
									left: '14px',
									top: '14px',
									pointerEvents: 'none',
									color: 'black !important',
								}}
							>
								{'Selecciona un estado'}
							</InputLabel>
						) : null}

						<Select
							id="numberOfInstallments"
							value={selectedState}
							IconComponent={CustomIconComponent}
							sx={{
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: '#C9C9C9',
								},
								'.MuiOutlinedInput-root': {
									'&:hover': {
										backgroundColor: 'transparent',
									},
								},
								'.MuiOutlinedInput-notchedOutline': {
									borderColor: '#C9C9C9',
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: '#0467D2',
								},
								'& .MuiSelect-iconOpen': {
									transform: 'none',
								},
								width: '100%',
								maxWidth: '368px',
								height: '48px',
							}}
							onChange={handleStateChange}
						>
							<MenuItem value="">
								<em>{'Selecciona un estado'}</em>
							</MenuItem>
							{states.map((state, index) => (
								<MenuItem key={index} value={state}>
									{state}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					{stores.length < 29 ? (
						<FormControl variant="outlined" fullWidth>
							{!selectedStore ? (
								<InputLabel
									htmlFor="numberOfInstallments"
									sx={{
										transform: 'none',
										position: 'absolute',
										left: '14px',
										top: '14px',
										pointerEvents: 'none',
										color: 'black !important',
									}}
								>
									{'Selecciona una tienda'}
								</InputLabel>
							) : null}
							<Select
								id="numberOfInstallments"
								value={selectedStore}
								IconComponent={CustomIconComponent}
								sx={{
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: '#C9C9C9',
									},
									'.MuiOutlinedInput-root': {
										'&:hover': {
											backgroundColor: 'transparent',
										},
									},
									'.MuiOutlinedInput-notchedOutline': {
										borderColor: '#C9C9C9',
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: '#0467D2',
									},
									'& .MuiSelect-iconOpen': {
										transform: 'none',
									},

									width: '100%',
									maxWidth: '368px',
									height: '48px',
								}}
								onChange={(e) => handleStore(e.target.value)}
							>
								<MenuItem value="">
									<em>{'Selecciona una tienda'}</em>
								</MenuItem>
								{stores.map((state, index) => (
									<MenuItem key={index} value={state.uuid}>
										{state.name}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					) : null}
				</Grid>
			</Grid>
		</DpContainer>
	);
};
